import React, { useState, useEffect } from 'react';
import { adminService } from '../services/firebaseService';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  createdAt: any;
  createdBy: string;
  lastLoginAt: any;
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    appName: 'Datezy Admin',
    maintenanceMode: false,
    allowUserRegistration: true,
    maxFileSize: 10,
    sessionTimeout: 24
  });

  // Admin yönetimi state'leri
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    name: '',
    role: 'admin',
    permissions: ['read', 'write'],
    password: '',
    useAutoPassword: true
  });
  const [createdAdminInfo, setCreatedAdminInfo] = useState<{
    email: string;
    name: string;
    password: string;
  } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Admin listesini yükle
  const loadAdmins = async () => {
    try {
      setLoading(true);
      const adminList = await adminService.getAllAdmins();
      setAdmins(adminList);
    } catch (error) {
      console.error('Admin listesi yüklenirken hata:', error);
      alert('Admin listesi yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Component mount olduğunda admin listesini yükle
  useEffect(() => {
    if (activeTab === 'admin-users') {
      loadAdmins();
    }
  }, [activeTab]);

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    // Save settings logic here
    alert('Ayarlar kaydedildi!');
  };

  // Yeni admin ekleme
  const handleAddAdmin = async () => {
    try {
      if (!newAdmin.email || !newAdmin.name) {
        alert('Email ve isim alanları zorunludur');
        return;
      }

      // Manuel şifre kontrolü
      if (!newAdmin.useAutoPassword && (!newAdmin.password || newAdmin.password.length < 6)) {
        alert('Manuel şifre en az 6 karakter olmalıdır');
        return;
      }

      setLoading(true);
      const result = await adminService.addAdmin({
        email: newAdmin.email,
        name: newAdmin.name,
        role: newAdmin.role,
        permissions: newAdmin.permissions,
        password: newAdmin.useAutoPassword ? undefined : newAdmin.password,
        createdBy: 'current-admin-id' // Bu gerçek oturumdan alınmalı
      });

      setShowAddAdminModal(false);
      setNewAdmin({ email: '', name: '', role: 'admin', permissions: ['read', 'write'], password: '', useAutoPassword: true });
      await loadAdmins();
      
      // Tip uyumlu hale getir
      if (result && 'password' in result) {
        setCreatedAdminInfo({
          email: result.email,
          name: result.name,
          password: result.password
        });
        setShowPasswordModal(true);
      }
      
      alert('Admin başarıyla eklendi');
    } catch (error: any) {
      console.error('Admin ekleme hatası:', error);
      alert(error.message || 'Admin eklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Admin güncelleme
  const handleUpdateAdmin = async () => {
    try {
      if (!editingAdmin) return;

      setLoading(true);
      await adminService.updateAdmin(editingAdmin.id, {
        name: editingAdmin.name,
        role: editingAdmin.role,
        permissions: editingAdmin.permissions,
        isActive: editingAdmin.isActive
      });

      setEditingAdmin(null);
      await loadAdmins();
      alert('Admin başarıyla güncellendi');
    } catch (error) {
      console.error('Admin güncelleme hatası:', error);
      alert('Admin güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Admin silme
  const handleDeleteAdmin = async (adminId: string, adminName: string) => {
    if (!window.confirm(`"${adminName}" isimli admini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setLoading(true);
      await adminService.deleteAdmin(adminId);
      await loadAdmins();
      alert('Admin başarıyla silindi');
    } catch (error) {
      console.error('Admin silme hatası:', error);
      alert('Admin silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'Genel', icon: '⚙️' },
    { id: 'security', label: 'Güvenlik', icon: '🔒' },
    { id: 'notifications', label: 'Bildirimler', icon: '🔔' },
    { id: 'admin-users', label: 'Admin Kullanıcıları', icon: '👥' }
  ];

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ 
            color: '#2c3e50', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            ⚙️ Sistem Ayarları
          </h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            Admin paneli ve uygulama ayarlarını yönetin
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem' }}>
        {/* Sidebar Tabs */}
        <div style={{
          width: '250px',
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '1rem',
          height: 'fit-content'
        }}>
          <h3 style={{ 
            margin: '0 0 1rem 0', 
            color: '#2c3e50',
            fontSize: '1.1rem',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '0.5rem'
          }}>
            Ayar Kategorileri
          </h3>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                margin: '0 0 0.5rem 0',
                backgroundColor: activeTab === tab.id ? '#F78D93' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#666',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{ flex: 1 }}>
          {activeTab === 'general' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              padding: '2rem'
            }}>
              <h3 style={{ 
                margin: '0 0 2rem 0', 
                color: '#2c3e50',
                fontSize: '1.3rem',
                borderBottom: '2px solid #F78D93',
                paddingBottom: '0.5rem'
              }}>
                Genel Ayarlar
              </h3>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  Uygulama Adı
                </label>
                <input
                  type="text"
                  value={settings.appName}
                  onChange={(e) => handleSettingChange('appName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e6ed',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#F78D93'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e6ed'}
                />
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50' }}>
                    Bakım Modu
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                    Bakım modunda uygulama geçici olarak kapalı olur
                  </p>
                </div>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.maintenanceMode ? '#F78D93' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.4s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '',
                      height: '18px',
                      width: '18px',
                      left: settings.maintenanceMode ? '29px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.4s'
                    }}></span>
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '2rem'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50' }}>
                    Kullanıcı Kaydına İzin Ver
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                    Yeni kullanıcıların kayıt olmasına izin verir
                  </p>
                </div>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.allowUserRegistration}
                    onChange={(e) => handleSettingChange('allowUserRegistration', e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.allowUserRegistration ? '#F78D93' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.4s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '',
                      height: '18px',
                      width: '18px',
                      left: settings.allowUserRegistration ? '29px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.4s'
                    }}></span>
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    Maksimum Dosya Boyutu (MB)
                  </label>
                  <input
                    type="number"
                    value={settings.maxFileSize}
                    onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e6ed',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    Oturum Zaman Aşımı (saat)
                  </label>
                  <input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e6ed',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                style={{
                  backgroundColor: '#F78D93',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                💾 Ayarları Kaydet
              </button>
            </div>
          )}

          {activeTab === 'security' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              padding: '2rem'
            }}>
              <h3 style={{ 
                margin: '0 0 2rem 0', 
                color: '#2c3e50',
                fontSize: '1.3rem',
                borderBottom: '2px solid #F78D93',
                paddingBottom: '0.5rem'
              }}>
                Güvenlik Ayarları
              </h3>
              <div style={{
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p>🔒 Güvenlik ayarları geliştiriliyor...</p>
                <p>Bu bölümde şifre politikaları, 2FA ayarları ve oturum yönetimi olacak.</p>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              padding: '2rem'
            }}>
              <h3 style={{ 
                margin: '0 0 2rem 0', 
                color: '#2c3e50',
                fontSize: '1.3rem',
                borderBottom: '2px solid #F78D93',
                paddingBottom: '0.5rem'
              }}>
                Bildirim Ayarları
              </h3>
              <div style={{
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p>🔔 Bildirim ayarları geliştiriliyor...</p>
                <p>Bu bölümde email bildirimleri, push bildirimleri ve admin uyarıları olacak.</p>
              </div>
            </div>
          )}

          {activeTab === 'admin-users' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              padding: '2rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  color: '#2c3e50',
                  fontSize: '1.3rem',
                  borderBottom: '2px solid #F78D93',
                  paddingBottom: '0.5rem'
                }}>
                  👥 Admin Kullanıcıları
                </h3>
                <button
                  onClick={() => setShowAddAdminModal(true)}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  ➕ Yeni Admin Ekle
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Yükleniyor...</p>
                </div>
              ) : (
                <div style={{
                  border: '1px solid #e0e6ed',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e6ed',
                          fontWeight: '600',
                          color: '#2c3e50'
                        }}>
                          İsim
                        </th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e6ed',
                          fontWeight: '600',
                          color: '#2c3e50'
                        }}>
                          Email
                        </th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e6ed',
                          fontWeight: '600',
                          color: '#2c3e50'
                        }}>
                          Rol
                        </th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'center',
                          borderBottom: '1px solid #e0e6ed',
                          fontWeight: '600',
                          color: '#2c3e50'
                        }}>
                          Durum
                        </th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'center',
                          borderBottom: '1px solid #e0e6ed',
                          fontWeight: '600',
                          color: '#2c3e50'
                        }}>
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin) => (
                        <tr key={admin.id} style={{
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          <td style={{ padding: '1rem' }}>
                            <strong>{admin.name}</strong>
                          </td>
                          <td style={{ padding: '1rem', color: '#666' }}>
                            {admin.email}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              backgroundColor: admin.role === 'super_admin' ? '#e3f2fd' : admin.role === 'moderator' ? '#fff3e0' : '#f8f9fa',
                              color: admin.role === 'super_admin' ? '#1976d2' : admin.role === 'moderator' ? '#e65100' : '#666'
                            }}>
                              {admin.role === 'super_admin' ? 'Süper Admin' : admin.role === 'moderator' ? 'Moderatör' : 'Admin'}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              backgroundColor: admin.isActive ? '#e8f5e8' : '#ffeaea',
                              color: admin.isActive ? '#28a745' : '#dc3545'
                            }}>
                              {admin.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <button
                              onClick={() => setEditingAdmin(admin)}
                              style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                marginRight: '0.5rem'
                              }}
                            >
                              ✏️ Düzenle
                            </button>
                            <button
                              onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                      {admins.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#666'
                          }}>
                            Henüz admin kullanıcı bulunmuyor.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Yeni Admin Ekleme Modal */}
      {showAddAdminModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '2rem',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              color: '#2c3e50'
            }}>
              ➕ Yeni Admin Ekle
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Email
              </label>
              <input
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
                placeholder="admin@datezy.com"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                İsim
              </label>
              <input
                type="text"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
                placeholder="Admin Adı"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Rol
              </label>
              <select
                value={newAdmin.role}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, role: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="moderator">Moderatör</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Süper Admin</option>
              </select>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50' }}>
                  Otomatik Şifre Oluştur
                </h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                  Güvenli şifre otomatik oluşturulsun mu?
                </p>
              </div>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '50px',
                height: '24px'
              }}>
                <input
                  type="checkbox"
                  checked={newAdmin.useAutoPassword}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, useAutoPassword: e.target.checked }))}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: newAdmin.useAutoPassword ? '#F78D93' : '#ccc',
                  borderRadius: '24px',
                  transition: '0.4s'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: newAdmin.useAutoPassword ? '29px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: '0.4s'
                  }}></span>
                </span>
              </label>
            </div>

            {!newAdmin.useAutoPassword && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500'
                }}>
                  Şifre
                </label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e6ed',
                    borderRadius: '6px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="En az 6 karakter"
                  minLength={6}
                />
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => setShowAddAdminModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                İptal
              </button>
              <button
                onClick={handleAddAdmin}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Düzenleme Modal */}
      {editingAdmin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '2rem',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              color: '#2c3e50'
            }}>
              ✏️ Admin Düzenle
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Email (değiştirilemez)
              </label>
              <input
                type="email"
                value={editingAdmin.email}
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa',
                  color: '#666'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                İsim
              </label>
              <input
                type="text"
                value={editingAdmin.name}
                onChange={(e) => setEditingAdmin(prev => prev ? { ...prev, name: e.target.value } : null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Rol
              </label>
              <select
                value={editingAdmin.role}
                onChange={(e) => setEditingAdmin(prev => prev ? { ...prev, role: e.target.value } : null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="moderator">Moderatör</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Süper Admin</option>
              </select>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50' }}>
                  Aktif Durum
                </h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                  Admin kullanıcının sisteme erişimini kontrol eder
                </p>
              </div>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '50px',
                height: '24px'
              }}>
                <input
                  type="checkbox"
                  checked={editingAdmin.isActive}
                  onChange={(e) => setEditingAdmin(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: editingAdmin.isActive ? '#F78D93' : '#ccc',
                  borderRadius: '24px',
                  transition: '0.4s'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: editingAdmin.isActive ? '29px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: '0.4s'
                  }}></span>
                </span>
              </label>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => setEditingAdmin(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                İptal
              </button>
              <button
                onClick={handleUpdateAdmin}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Güncelleniyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Admin Oluşturma Sonucu Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '2rem',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              color: '#2c3e50'
            }}>
              Yeni Admin Oluşturuldu
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Email
              </label>
              <input
                type="email"
                value={createdAdminInfo?.email}
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa',
                  color: '#666'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                İsim
              </label>
              <input
                type="text"
                value={createdAdminInfo?.name}
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa',
                  color: '#666'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Şifre
              </label>
              <input
                type="text"
                value={createdAdminInfo?.password}
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa',
                  color: '#666'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid #e0e6ed',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage; 